import { IPulseXConfig } from '@pulsex/types';

export const IS_LIVE_DATA = process.env.NEXT_PUBLIC_IS_PRODUCTION == 'true';

/**
 * Opt **out** on any deployment that is not the canonical public site.
 *
 * Previously `=== 'true' || true`, which is unconditionally true — the variable
 * has never had any effect. Defaulting to on keeps production working with no
 * env change; staging sets `NEXT_PUBLIC_ALLOW_CRAWL=false`.
 */
export const IS_ALLOW_CRAWL = process.env.NEXT_PUBLIC_ALLOW_CRAWL !== 'false';
export const IS_LOCAL = process.env.NODE_ENV === 'development' && false;

/**
 * Which CMS to read and which host is canonical. Unchanged: `IS_ALLOW_CRAWL`
 * was always true, so this is exactly what `IS_PRODUCTION` evaluated to before.
 */
export const IS_PRODUCTION = IS_LIVE_DATA && !IS_LOCAL;

/**
 * True only on the canonical public deployment. Staging (`develop.al-akhbar.com`)
 * builds with `NEXT_PUBLIC_IS_PRODUCTION=true` so it can serve live CMS content,
 * which also made it take the production branch of every `IS_PRODUCTION` check:
 * it was crawlable, and it reported into the production analytics properties.
 *
 * Indexing and analytics key off this instead, so a non-canonical deployment can
 * serve real content without being indexed or counted.
 */
export const IS_CANONICAL_DEPLOYMENT = IS_PRODUCTION && IS_ALLOW_CRAWL;

const pulseConfig: IPulseXConfig = {
    CLIENT_ID: 'akhbar',
    CLIENT_NAME: 'الأخبار',
    LANGUAGE: 'ar',
    CLIENT_DOMAIN: 'al-akhbar.com',
    API_URL: IS_PRODUCTION ? 'https://public-api.al-akhbar.com' : 'https://staging-api.al-akhbar.com',
    MEDIA_URL: IS_PRODUCTION ? 'https://media.al-akhbar.com' : 'https://beta-media.al-akhbar.com',
    CLIENT_URL: IS_PRODUCTION ? 'https://www.al-akhbar.com' : 'https://alakhbar-production.up.railway.app',
    weatherApiKey: 'cf681607a7c35bd1bac01b122bf3308c',
    SEARCH_URL: 'https://es.al-akhbar.com/api',
    TRACKER_BASE_URL: 'track.al-akhbar.com',
    TRACK_BASE_URL: 'track.al-akhbar.com',
    IS_PRODUCTION: IS_PRODUCTION,
    IS_INDEXABLE: IS_CANONICAL_DEPLOYMENT,
    cropRatios: {
        square: 1,
        wide: 2,
        medium: 3,
        cinema: 4,
        rectangle: 5,
    },
    cropSizes: {
        '1': 1,
        '2': 1.7,
        '3': 1.5,
        '4': 0.657,
        '5': 1.777,
    },
    /**
     * Document-title / meta-description shaping (see `@pulsex/metadata`).
     * The CMS ships bare labels for listing pages and repeats one headline
     * across the recurring opinion columns, which leaves several live pages
     * sharing a `<title>`; these rules make each one descriptive and unique.
     */
    seo: {
        brand: 'الأخبار',
        separator: ' | ',
        minTitleLength: 30,
        maxTitleLength: 60,
        maxDescriptionLength: 160,
        titleSuffixes: {
            author: 'مقالات وتقارير الكاتب',
            category: 'أخبار وتحليلات',
            newspaper: 'أرشيف أعداد جريدة الأخبار',
            picture: 'ألبومات الصور',
            video: 'تقارير ومقاطع مصوّرة',
            coverage: 'ملفات وتغطيات خاصة',
        },
        fallbackDescriptionSuffix: 'تابعوا آخر الأخبار والتقارير والتحليلات على موقع جريدة الأخبار.',
        // `/category/<x>/<tag>` renders «<x> #<tag>» but returns the same
        // articles for any tag, real or invented, so each of those URLs was a
        // copy of the category claiming to be its own canonical. Point them at
        // the category instead.
        filteredListingPrefixes: ['category'],
        // Levantine month names, January first. Recurring columns such as
        // «على بالي» share one headline and one byline, so the publication
        // date is what tells the instalments apart.
        monthNames: [
            'كانون الثاني',
            'شباط',
            'آذار',
            'نيسان',
            'أيار',
            'حزيران',
            'تموز',
            'آب',
            'أيلول',
            'تشرين الأول',
            'تشرين الثاني',
            'كانون الأول',
        ],
    },
    // TODO: edit this to the real date
    sitemapStartingDate: '2024-10-01',
    QUERY_KEYS: {
        GET_PAGE: 'GET_PAGE',
        GET_ASSETS: 'GET_ASSETS',
        GET_DAILY_PDF: 'GET_DAILY_PDF',
    },
};

export default pulseConfig;
